"use client"

import { motion } from "framer-motion"
import { Link2, Brain, Zap, BarChart3 } from "lucide-react"

export function HowItWorksSection() {
  const steps = [
    {
      number: "01",
      icon: Link2,
      title: "Быстрое подключение",
      subtitle: "5 минут",
      description:
        "Подключаете Instagram Business аккаунт через официальное подключение. Один клик - никаких сложных настроек. Система автоматически получает доступ к вашим сообщениям с вашего разрешения.",
      details: [
        "Официальное подключение через Instagram API",
        "Полное соответствие политикам безопасности Meta",
        "Сертифицированный партнер",
        "Ваши данные защищены и зашифрованы",
      ],
    },
    {
      number: "02",
      icon: Brain,
      title: "AI изучает ваш уникальный стиль",
      subtitle: "24 часа",
      description:
        "Система анализирует последние 500-1000 ваших ответов и изучает как вы общаетесь: приветствия, эмодзи, терминологию, структуру ответов. AI создает response templates в вашем стиле.",
      details: [
        "Анализ вашего brand voice и тона общения",
        "Категоризация типов вопросов (наличие, размеры, доставка)",
        "Создание естественных вариантов ответов",
        "Загрузка каталога продуктов (Shopify, WooCommerce и др.)",
        "Настройка правил и специфических фраз",
      ],
    },
    {
      number: "03",
      icon: Zap,
      title: "Автоматическая обработка 24/7",
      subtitle: "Мгновенно",
      description:
        "Каждое новое сообщение обрабатывается за 5-10 секунд: анализ намерения клиента, проверка наличия товара в реальном времени, генерация персонализированного ответа в вашем стиле.",
      details: [
        "Мгновенное получение сообщений (0-2 секунды)",
        "Анализ контекста и эмоционального тона",
        "Проверка актуальной информации о товарах",
        "Персонализация с именем клиента",
        "Умная передача сложных случаев человеку",
        "Push-уведомления о важных сообщениях",
      ],
    },
    {
      number: "04",
      icon: BarChart3,
      title: "Постоянное обучение",
      subtitle: "Непрерывно",
      description:
        "Система постоянно учится на ваших корректировках. Если вы исправите ответ, InstaSell запомнит это и будет отвечать лучше. Через месяц точность достигает 95%.",
      details: [
        "Детальная аналитика по категориям вопросов",
        "Процент конверсии от контакта до покупки",
        "Калькулятор экономии времени и денег",
        "Тестирование вариантов ответов (A/B testing)",
        "Адаптация к сезонным событиям и акциям",
      ],
    },
  ]

  return (
    <section id="how-it-works" className="py-24 px-6 bg-background">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-20">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="font-serif text-5xl md:text-6xl mb-6 text-balance"
          >
            Запустите за 5 минут, результат увидите мгновенно
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed"
          >
            InstaSell — это не просто chatbot, а полноценная AI-система управления клиентским сервисом в Instagram,
            которая изучает именно ваш бизнес, ваш стиль общения и ваши продукты
          </motion.p>
        </div>

        <div className="relative">
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px bg-border hidden md:block" />

          <div className="space-y-20">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, x: index % 2 === 0 ? -40 : 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`relative flex flex-col md:flex-row items-start md:items-center gap-8 ${
                  index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                }`}
              >
                <div className="flex-shrink-0 w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-serif text-xl font-bold relative z-10 md:absolute md:left-1/2 md:-translate-x-1/2">
                  {step.number}
                </div>

                <div
                  className={`flex-1 ${index % 2 === 0 ? "md:pr-16 md:text-right" : "md:pl-16 md:text-left"} md:w-1/2`}
                >
                  <div className="bg-secondary p-8 rounded-2xl border border-border hover:border-accent/50 transition-colors">
                    <div className="flex items-center gap-3 mb-4 justify-start">
                      <step.icon className="w-8 h-8 text-accent" />
                      <span className="text-sm font-semibold text-accent">{step.subtitle}</span>
                    </div>
                    <h3 className="font-serif text-2xl md:text-3xl mb-4">{step.title}</h3>
                    <p className="text-muted-foreground leading-relaxed mb-6">{step.description}</p>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      {step.details.map((detail, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-accent mt-1">•</span>
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="hidden md:block md:w-1/2" />
              </motion.div>
            ))}
          </div>
        </div>

        {/* Additional Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="mt-24 bg-secondary rounded-3xl p-8 md:p-12 border border-border"
        >
          <h3 className="font-serif text-3xl md:text-4xl mb-8 text-center">Дополнительные возможности</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex gap-4">
              <div className="w-2 h-2 rounded-full bg-accent mt-2 flex-shrink-0" />
              <div>
                <h4 className="font-semibold mb-2">Управление несколькими аккаунтами</h4>
                <p className="text-sm text-muted-foreground">
                  Управляйте несколькими Instagram аккаунтами из одной панели
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-2 h-2 rounded-full bg-accent mt-2 flex-shrink-0" />
              <div>
                <h4 className="font-semibold mb-2">Распознавание голосовых сообщений</h4>
                <p className="text-sm text-muted-foreground">
                  Автоматическая расшифровка голосовых сообщений и ответ текстом
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-2 h-2 rounded-full bg-accent mt-2 flex-shrink-0" />
              <div>
                <h4 className="font-semibold mb-2">Распознавание изображений</h4>
                <p className="text-sm text-muted-foreground">
                  Клиент отправляет фото товара - система распознает и дает информацию о наличии
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-2 h-2 rounded-full bg-accent mt-2 flex-shrink-0" />
              <div>
                <h4 className="font-semibold mb-2">Интеграции с e-commerce</h4>
                <p className="text-sm text-muted-foreground">
                  Shopify, WooCommerce, Google Таблицы, Telegram, Slack и другие
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
